import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock3, MessageSquare, Plus, Send, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { pt } from "date-fns/locale";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUsers } from "@/lib/hooks";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export function CollaborationPanel({ entityType, entityId, task, project }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: users = [] } = useUsers();
  const [comment, setComment] = useState("");
  const [mentionIds, setMentionIds] = useState([]);
  const [minutes, setMinutes] = useState("");
  const [timeNotes, setTimeNotes] = useState("");
  const { data: comments = [] } = useQuery({
    queryKey: ["comments", entityType, entityId],
    queryFn: async () => (await api.get("/comments", { params: { entityType, entityId } })).data,
    enabled: Boolean(entityId),
  });
  const { data: entries = [] } = useQuery({
    queryKey: ["time-entries", entityType, entityId],
    queryFn: async () => (await api.get("/time-entries", { params: entityType === "TASK" ? { taskId: entityId } : { projectId: entityId } })).data,
    enabled: Boolean(entityId),
  });

  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post("/comments", { entityType, entityId, content: comment, mentionIds });
      setComment("");
      setMentionIds([]);
      queryClient.invalidateQueries({ queryKey: ["comments", entityType, entityId] });
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    }
  };
  const deleteComment = async (commentId) => {
    await api.delete(`/comments/${commentId}`);
    queryClient.invalidateQueries({ queryKey: ["comments", entityType, entityId] });
  };
  const logTime = async () => {
    const totalMinutes = Number(minutes);
    if (!totalMinutes) return toast.error("Indique o tempo em minutos");
    try {
      await api.post("/time-entries", {
        taskId: entityType === "TASK" ? entityId : "",
        projectId: entityType === "PROJECT" ? entityId : task?.projectId || project?.id || "",
        clientId: task?.clientId || project?.clientId || "",
        date: new Date().toISOString().slice(0, 10),
        minutes: totalMinutes,
        notes: timeNotes,
      });
      setMinutes("");
      setTimeNotes("");
      queryClient.invalidateQueries({ queryKey: ["time-entries", entityType, entityId] });
      toast.success("Tempo registado");
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    }
  };
  const totalMinutes = entries.reduce((sum, entry) => sum + Number(entry.minutes || 0), 0);

  return (
    <Tabs defaultValue="comments" className="border-t border-border pt-4">
      <TabsList className="mb-3">
        <TabsTrigger value="comments"><MessageSquare className="mr-1.5 h-4 w-4" /> Comentários ({comments.length})</TabsTrigger>
        <TabsTrigger value="time"><Clock3 className="mr-1.5 h-4 w-4" /> Tempo ({Math.round(totalMinutes / 6) / 10}h)</TabsTrigger>
      </TabsList>
      <TabsContent value="comments" className="space-y-3">
        <div className="max-h-52 space-y-3 overflow-y-auto pr-1">
          {comments.map((item) => (
            <div key={item.id} className="flex gap-2.5">
              <UserAvatar user={{ name: item.userName, avatarUrl: item.userAvatarUrl }} size="h-7 w-7" />
              <div className="min-w-0 flex-1 rounded-xl bg-muted/40 px-3 py-2">
                <div className="flex items-center gap-2"><span className="text-xs font-semibold">{item.userName}</span><span className="text-[10px] text-muted-foreground">{item.created_at ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: pt }) : ""}</span>{(item.userId === user.id || ["ADMIN", "SUB_ADMIN"].includes(user.role)) && <button onClick={() => deleteComment(item.id)} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 className="h-3 w-3" /></button>}</div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{item.content}</p>
              </div>
            </div>
          ))}
          {comments.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">Ainda não existem comentários.</p>}
        </div>
        <Textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Escreve um comentário ou menciona alguém..." className="min-h-[70px]" />
        <div className="flex flex-wrap items-center gap-1.5">
          {users.filter((member) => member.id !== user.id).map((member) => {
            const selected = mentionIds.includes(member.id);
            return <button key={member.id} type="button" onClick={() => setMentionIds(selected ? mentionIds.filter((id) => id !== member.id) : [...mentionIds, member.id])} className={`rounded-full border px-2 py-1 text-[10px] ${selected ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>@{member.name.split(" ")[0]}</button>;
          })}
          <Button size="sm" onClick={sendComment} className="ml-auto h-8 bg-primary text-black hover:bg-primary/90"><Send className="mr-1 h-3.5 w-3.5" /> Comentar</Button>
        </div>
      </TabsContent>
      <TabsContent value="time" className="space-y-3">
        <div className="grid grid-cols-[110px_1fr_auto] gap-2"><Input type="number" min="1" value={minutes} onChange={(event) => setMinutes(event.target.value)} placeholder="Minutos" /><Input value={timeNotes} onChange={(event) => setTimeNotes(event.target.value)} placeholder="Em que trabalhaste?" /><Button onClick={logTime}><Plus className="mr-1 h-4 w-4" /> Registar</Button></div>
        <div className="max-h-48 space-y-2 overflow-y-auto">
          {entries.map((entry) => <div key={entry.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2"><Clock3 className="h-4 w-4 text-primary" /><div className="min-w-0 flex-1"><div className="truncate text-xs font-medium">{entry.notes || "Tempo de trabalho"}</div><div className="text-[10px] text-muted-foreground">{entry.userName} • {entry.date}</div></div><div className="text-xs font-semibold">{Math.floor(entry.minutes / 60)}h {entry.minutes % 60}min</div></div>)}
          {entries.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">Ainda não existe tempo registado.</p>}
        </div>
      </TabsContent>
    </Tabs>
  );
}
